import { FormsyInput } from '@thanx/uikit/input'
import { Radio, RadioGroup } from '@thanx/uikit/radio'
import { Switch } from '@thanx/uikit/switch'
import TooltipLabel from 'components/TooltipLabel'
import { buildTranslate } from 'locales'
import { I18n } from 'react-redux-i18n'
import { DeliveryFeeMode, FormModel, useFormModel } from '../FormModel'
import Card from './Card'
import NextButton from './NextButton'

type Props = {
  isDisabled: boolean
  isOpen: boolean
  isCurrent: boolean
  isValidForm: boolean
  onNext: () => void
  onFormChange: (newFormModel: FormModel, isChanged: boolean) => void
}

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.delivery_discount_card'
)

function titleForMode(): string {
  return t('title')
}

const DeliveryDiscountCard = ({
  isDisabled,
  isOpen,
  isCurrent,
  isValidForm,
  onNext,
  onFormChange,
}: Props) => {
  const { model, setModel } = useFormModel()
  const mode: DeliveryFeeMode = model.deliveryFeeMode || 'amount'

  const handleModeChange = (value: string) => {
    if (isDisabled) return
    const next: FormModel = {
      ...model,
      deliveryFeeMode: value as DeliveryFeeMode,
      discount: '',
      priceCap: '',
    }
    setModel(next)
    onFormChange(next, true)
  }

  return (
    <Card title={titleForMode()} isOpen={isOpen}>
      <p className="body-text-4 grey-70 mt-n3 mb-m">{t('subtitle')}</p>
      <RadioGroup
        name="deliveryFeeMode"
        aria-label={t('mode_group_label')}
        value={mode}
        onChange={value => handleModeChange(value)}
        disabled={isDisabled}
      >
        <Radio value="amount">
          <span className="font-weight-bold">{t('mode_amount_label')}</span>
          <p className="body-text-4 grey-70 mt-xxs mb-0">
            {t('mode_amount_help')}
          </p>
        </Radio>
        <Radio value="percent">
          <span className="font-weight-bold">{t('mode_percent_label')}</span>
          <p className="body-text-4 grey-70 mt-xxs mb-0">
            {t('mode_percent_help')}
          </p>
        </Radio>
        <Radio value="fixed_price">
          <span className="font-weight-bold">{t('mode_fixed_label')}</span>
          <p className="body-text-4 grey-70 mt-xxs mb-0">
            {t('mode_fixed_help')}
          </p>
        </Radio>
      </RadioGroup>

      <Switch condition={mode === 'amount'}>
        <FormsyInput
          // @ts-ignore
          className="mt-l"
          name="discount"
          disabled={isDisabled}
          label={
            <TooltipLabel
              label={t('discount_amount_label')}
              tooltip={t('mode_amount_help')}
            />
          }
          startEnhancer={t('dollar')}
          value={model.discount}
          validations={{ greaterThan: 0, isRequired: true, isNumeric: true }}
          validationErrors={{
            greaterThan: I18n.t('validations.greater_than', { value: 0 }),
            isRequired: I18n.t('validations.is_required'),
            isNumeric: I18n.t('validations.is_numeric'),
          }}
        />
      </Switch>

      <Switch condition={mode === 'percent'}>
        <FormsyInput
          // @ts-ignore
          className="mt-l"
          name="discount"
          disabled={isDisabled}
          label={
            <TooltipLabel
              label={t('discount_amount_label')}
              tooltip={t('mode_percent_help')}
            />
          }
          endEnhancer={t('percent')}
          value={model.discount}
          validations={{
            greaterThan: 0,
            lessThan: 101,
            isRequired: true,
            isNumeric: true,
          }}
          validationErrors={{
            greaterThan: t('percent_must_be'),
            lessThan: t('percent_must_be'),
            isRequired: I18n.t('validations.is_required'),
            isNumeric: I18n.t('validations.is_numeric'),
          }}
        />
      </Switch>

      <Switch condition={mode === 'fixed_price'}>
        <FormsyInput
          // @ts-ignore
          className="mt-l"
          name="priceCap"
          disabled={isDisabled}
          label={
            <TooltipLabel
              label={t('delivery_fee_label')}
              tooltip={t('mode_fixed_help')}
            />
          }
          startEnhancer={t('dollar')}
          value={model.priceCap}
          validations={{
            greaterThanOrEqual: 0,
            isRequired: true,
            isNumeric: true,
          }}
          validationErrors={{
            greaterThanOrEqual: I18n.t('validations.greater_than_or_equal', {
              value: 0,
            }),
            isRequired: I18n.t('validations.is_required'),
            isNumeric: I18n.t('validations.is_numeric'),
          }}
        />
      </Switch>

      <Switch condition={isCurrent}>
        <NextButton disabled={!isValidForm} onClick={onNext} />
      </Switch>
    </Card>
  )
}

export default DeliveryDiscountCard
